import React, { useEffect, useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import roomsService from '../services/roomsService'
import { useTranslation } from '../hooks/use-i18n'
import type { Room } from '../types/room'

interface RoomRulesModalProps {
  isOpen: boolean
  roomId: string
  onClose: () => void
  demoRoom?: {
    name: string
    type: Room['type']
    leagues: Room['leagues']
    setting: FootballSetting
  } | null
}

type FootballSetting = Record<string, number | string | boolean | undefined>

const VICTORY_SCORES: { key: string; label: string }[] = [
  { key: 'oneZero', label: '1-0' },
  { key: 'twoZero', label: '2-0' },
  { key: 'twoOne', label: '2-1' },
  { key: 'threeZero', label: '3-0' },
  { key: 'threeOne', label: '3-1' },
  { key: 'threeTwo', label: '3-2' },
  { key: 'fourZero', label: '4-0' },
  { key: 'fourOne', label: '4-1' },
  { key: 'fourTwo', label: '4-2' },
  { key: 'fourThree', label: '4-3' },
  { key: 'fiveAndMore', label: '5+' },
]

const DRAW_SCORES: { key: string; label: string }[] = [
  { key: 'zeroZero', label: '0-0' },
  { key: 'oneOne', label: '1-1' },
  { key: 'twoTwo', label: '2-2' },
  { key: 'threeThree', label: '3-3' },
  { key: 'fourFour', label: '4-4' },
  { key: 'fiveFiveMore', label: '5+' },
]

function settingValue(setting: FootballSetting | undefined, key: string, fallback: string): string {
  const v = setting?.[key]
  return v != null && v !== '' ? String(v) : fallback
}

function settingBool(setting: FootballSetting | undefined, key: string, fallback: boolean): boolean {
  const v = setting?.[key]
  if (v === true || v === 'true' || v === 1 || v === '1') return true
  if (v === false || v === 'false' || v === 0 || v === '0') return false
  return fallback
}

function scorePointsLabel(value: string, t: (key: string) => string): string {
  if (value === '—') return ''
  return Number(value) === 1 ? t('roomRules.pointSingular') : t('roomRules.pointPlural')
}

function ScorePill({
  label,
  value,
  variant,
  pointsLabel,
}: {
  label: string
  value: string
  variant: 'green' | 'purple'
  pointsLabel: string
}) {
  const pillClass = variant === 'green'
    ? 'bg-emerald-500/15 border-white/10'
    : 'bg-purple-500/15 border-white/10'
  const pointsClass = variant === 'green' ? 'text-emerald-300' : 'text-purple-300'

  return (
    <span className={`inline-flex items-center text-xs sm:text-sm px-3 py-1.5 rounded-lg border ${pillClass}`}>
      <span className="font-bold text-slate-50">{label}</span>
      <span className="text-white/45 mx-1">:</span>
      {value === '—' ? (
        <span className="text-white/45">—</span>
      ) : (
        <span className={`font-bold ${pointsClass}`}>
          {value} {pointsLabel}
        </span>
      )}
    </span>
  )
}

const RoomRulesModal: React.FC<RoomRulesModalProps> = ({ isOpen, roomId, onClose, demoRoom = null }) => {
  const { t } = useTranslation()
  const [room, setRoom] = useState<Room | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || demoRoom || !roomId) return
    let cancelled = false
    setLoading(true)
    roomsService
      .getRoom(roomId, { skipCache: true })
      .then((data) => {
        if (!cancelled) setRoom(data)
      })
      .catch(() => {
        if (!cancelled) setRoom(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, roomId, demoRoom])

  if (!isOpen) return null

  const activeRoom = demoRoom ?? room
  const setting = (activeRoom?.setting ?? {}) as FootballSetting
  const showFutureChampionStart = settingBool(setting, 'enableFutureChampionStart', true)
  const showFutureChampionSecond = settingBool(setting, 'enableFutureChampionSecondChance', true)
  const showTopScorer = settingBool(setting, 'enableTopScorer', false)

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="blue-sky-card w-full max-w-lg max-h-[85vh] rounded-2xl flex flex-col shadow-xl">
        <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-white/20 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xl" aria-hidden>
              📋
            </span>
            <h2 className="text-lg sm:text-xl font-display font-bold text-white truncate">
              {t('roomRules.title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-white/80 hover:text-white transition-colors shrink-0"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 sm:px-6 py-5">
          {!demoRoom && loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-white/80">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm">{t('roomRules.loading')}</p>
            </div>
          ) : !activeRoom ? (
            <p className="text-center text-white/80 py-12">{t('roomRules.loadFailed')}</p>
          ) : (
            <div className="space-y-6">
              <section className="rounded-xl bg-white/10 border border-white/20 p-4">
                <h3 className="text-sm font-bold text-sky-300 mb-3">🏠 {t('roomRules.roomInfo')}</h3>
                <p className="text-sm text-white/90 mb-1">
                  <span className="font-semibold text-amber-300">{t('roomRules.name')}:</span> {activeRoom.name}
                </p>
                <p className="text-sm text-white/90">
                  <span className="font-semibold text-amber-300">{t('roomRules.type')}:</span> {activeRoom.type}
                </p>
              </section>

              <section>
                <h3 className="text-sm font-bold text-emerald-300 mb-3">🏆 {t('roomRules.pointsSystem')}</h3>

                <div className="space-y-3 mb-4">
                  <div className="rounded-xl bg-white/10 border border-white/20 p-4">
                    <p className="text-xs font-bold text-emerald-400 mb-1">🎯 {t('roomRules.winnerPrediction')}</p>
                    <p className="text-sm text-white/90 mb-2">{t('roomRules.winnerPredictionDesc')}</p>
                    <p className="text-sm font-bold text-emerald-300">
                      {t('roomRules.points')}: {settingValue(setting, 'correctResultPoints', '1')}
                    </p>
                  </div>
                  <div className="rounded-xl bg-white/10 border border-white/20 p-4">
                    <p className="text-xs font-bold text-purple-300 mb-1">⚖️ {t('roomRules.drawPrediction')}</p>
                    <p className="text-sm text-white/90 mb-2">{t('roomRules.drawPredictionDesc')}</p>
                    <p className="text-sm font-bold text-purple-300">
                      {t('roomRules.points')}: {settingValue(setting, 'correctDrawPoints', '1')}
                    </p>
                  </div>
                </div>

                <div className="rounded-xl bg-white/10 border border-white/20 p-4 mb-3">
                  <p className="text-xs font-bold text-emerald-400 mb-3">⚽ {t('roomRules.victoryScores')}</p>
                  <div className="flex flex-wrap gap-2">
                    {VICTORY_SCORES.map(({ key, label }) => {
                      const pts = settingValue(setting, key, '—')
                      return (
                        <ScorePill
                          key={key}
                          label={label}
                          value={pts}
                          variant="green"
                          pointsLabel={scorePointsLabel(pts, t)}
                        />
                      )
                    })}
                  </div>
                </div>

                <div className="rounded-xl bg-white/10 border border-white/20 p-4 mb-3">
                  <p className="text-xs font-bold text-purple-300 mb-3">⚖️ {t('roomRules.drawScores')}</p>
                  <div className="flex flex-wrap gap-2">
                    {DRAW_SCORES.map(({ key, label }) => {
                      const pts = settingValue(setting, key, '—')
                      return (
                        <ScorePill
                          key={key}
                          label={label}
                          value={pts}
                          variant="purple"
                          pointsLabel={scorePointsLabel(pts, t)}
                        />
                      )
                    })}
                  </div>
                </div>

                {(showFutureChampionStart || showFutureChampionSecond || showTopScorer) && (
                  <div className="rounded-xl bg-white/10 border border-white/20 p-4 mb-3">
                    <p className="text-xs font-bold text-yellow-300 mb-3">👑 {t('roomRules.tournamentExtras')}</p>
                    <div className="space-y-3">
                      {showFutureChampionStart && (
                        <div>
                          <p className="text-sm text-white/90">{t('createRoom.enableFutureChampionStart')}</p>
                          <p className="text-sm font-bold text-yellow-300">
                            {t('roomRules.points')}: {settingValue(setting, 'championAtStart', '20')}
                          </p>
                        </div>
                      )}
                      {showFutureChampionSecond && (
                        <div>
                          <p className="text-sm text-white/90">{t('createRoom.enableFutureChampionSecondChance')}</p>
                          <p className="text-sm font-bold text-yellow-300">
                            {t('roomRules.points')}: {settingValue(setting, 'championSecondChance', '10')}
                          </p>
                        </div>
                      )}
                      {showTopScorer && (
                        <div>
                          <p className="text-sm text-white/90">{t('createRoom.enableTopScorer')}</p>
                          <p className="text-sm font-bold text-yellow-300">
                            {t('roomRules.points')}: {settingValue(setting, 'topScorerPoints', '20')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="rounded-xl bg-white/10 border border-white/20 p-4">
                  <p className="text-xs font-bold text-cyan-300 mb-3">🏁 {t('roomRules.playoffPoints')}</p>
                  <div className="space-y-2">
                    <p className="text-sm text-white/90">
                      {t('roomRules.winnerAdditionalTime')}:{' '}
                      <span className="font-bold text-cyan-300">
                        {settingValue(setting, 'winnerAdditionalTime', '3')} {t('roomRules.points').toLowerCase()}
                      </span>
                    </p>
                    <p className="text-sm text-white/90">
                      {t('roomRules.winnerPenalties')}:{' '}
                      <span className="font-bold text-cyan-300">
                        {settingValue(setting, 'winnerPenalties', '5')} {t('roomRules.points').toLowerCase()}
                      </span>
                    </p>
                  </div>
                </div>
              </section>

              <section className="rounded-xl bg-white/10 border border-white/20 p-4">
                <h3 className="text-xs font-bold text-cyan-300 mb-3">💡 {t('roomRules.howPointsWork')}</h3>
                <ul className="text-sm text-white/90 space-y-2 list-none">
                  <li>• {t('roomRules.howPoints1')}</li>
                  <li>• {t('roomRules.howPoints2')}</li>
                  <li>• {t('roomRules.howPoints3')}</li>
                  <li>• {t('roomRules.howPoints4')}</li>
                  <li>• {t('roomRules.howPoints5')}</li>
                </ul>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RoomRulesModal
